// React
import { Fragment, useState } from "react"

// Components
import { BaseTable, Button, Label, Loading, NumberInput, TableHeader, TableSorting } from "components"
import { PoButton } from "./PoButton"

// Icons
import { TbChevronDown } from "react-icons/tb"

// Types
import type { DataType } from "../types"

// Utils
import { useToggle } from "utils"
import { getCurrentRow, useComponentList } from "../utils"
import { PERMISION } from "security"

export function RowSection(params: {
  currentPage: number
  index: number
  item: DataType
  size: number
  tableActive: TableSorting[]
}): JSX.Element {
  // Vars
  const item = params.item
  const build_item = item.build_item
  const product_id = item.product_id
  const qty_order: number = Math.abs(item.available)

  // Hooks
  const showComponent = useToggle(false)

  // Functions
  const generateButton = (): JSX.Element => {
    // Vars
    const buy = item.buy
    const isPo: boolean = !build_item && buy
    const isComponent: boolean = build_item && !buy

    if (isPo) {
      return (
        <PoButton
          product_id={product_id}
          qty_order={qty_order}
        />
      )
    } else if (isComponent) {
      return (
        <Button
          permission={PERMISION.INVENTORY.REGISTER.REQUIEST_FROM.UPDATE}
          className="w-full"
          color="success"
          onClick={showComponent.toggle}
        >
          <section className="flex flex-col items-center">
            <div>COMPONENT</div>
            <TbChevronDown />
          </section>
        </Button>
      )
    }

    return <></>
  }

  return (
    <>
      <tr>
        <td className="text-center">{getCurrentRow(params.currentPage, params.size, params.index)}</td>

        {/* Kolom filter */}
        {params.tableActive
          .filter((col) => col.isActive !== false)
          .map((col, idx) => (
            <Fragment key={idx}>
              {renderCellHeader(col.value!, item, build_item)}
            </Fragment>
          ))}
          
        <td className="w-[120px]">{generateButton()}</td>
      </tr>

      {showComponent.isActive && (
        <tr className="!bg-base-100">
          <td colSpan={11}>
            <ComponentSection
              product_id={product_id}
              qty_order={qty_order}
            />
          </td>
        </tr>
      )}
    </>
  )
}

function ComponentSection(params: {
  product_id: number
  qty_order: number
}): JSX.Element {
  // Vars
  const qty_order = params.qty_order

  // Hooks
  const { data, isLoading } = useComponentList(params.product_id, qty_order)
  const [qty, setQty] = useState<number>(qty_order)

  // Functions
  const multiplyQty = (value: number): number => {
    return value * qty
  }

  return (
    <section className="flex flex-col gap-3">
      <section className="flex gap-3 items-center">
        <Label>BUILD</Label>

        <NumberInput
          value={qty}
          onValueChange={(value) => setQty(value.floatValue || 0)}
        />
      </section>

      <BaseTable>
        <TableHeader>
          <tr>
            <th>NO</th>
            <th>CODE</th>
            <th>DESCRIPTION</th>
            <th>QTY</th>
            <th>UNIT</th>
            <th>SOH</th>
            <th>ON ORDER</th>
            <th>COMMITED</th>
            <th>AVAILABLE</th>
            <th>REQUEST</th>
          </tr>
        </TableHeader>

        <tbody>
          {isLoading || !data.length ? (
            <tr>
              <td colSpan={10}>
                <Loading
                  loading={isLoading}
                  errorText="No data available"
                />
              </td>
            </tr>
          ) : data.map((item, key) => (
            <tr key={key}>
              <td className="text-center">{key + 1}</td>
              <td className="text-center">{item.product_code}</td>
              <td>{item.product_name}</td>
              <td className="text-right">{multiplyQty(item.qty)}</td>
              <td>{item.mr_unit_name}</td>
              <td className="text-right">{multiplyQty(item.soh)}</td>
              <td className="text-right">{multiplyQty(item.back_order)}</td>
              <td className="text-right">{multiplyQty(item.commited)}</td>
              <td className="text-right">{multiplyQty(item.available)}</td>

              <td className="w-[120px]">
                <PoButton
                  product_id={item.product_ingredient_id}
                  qty_order={Math.abs(multiplyQty(item.available))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </BaseTable>
    </section>
  )
}

function renderCellHeader(title: string, item: DataType, build_item: boolean) {
  switch (title) {
    case 'item_code':
      return (
        <td className="text-center">{item.product_code}</td>
      )
    case 'barcode':
      return (
        <td className="text-center">{item.product_barcode}</td>
      )
    case 'build':
      return (
        <td>
          <div className="flex justify-center">
            <input
              type="checkbox"
              className="checkbox checkbox-primary"
              checked={build_item}
            />
          </div>
        </td>
      )
    case 'description':
      return (
        <td>{item.product_name}</td>
      );
    case 'unit':
      return (
        <td>{item.mr_unit_name}</td>
      );
    case 'soh':
      return (
        <td className="text-right">{item.soh}</td>
      )
    case 'on_order':
      return (
        <td className="text-right">{item.back_order}</td>
      );
    case 'commit':
      return (
        <td className="text-right">{item.commited}</td>
      );
    case 'available':
      return (
        <td className="text-right">{item.available}</td>
      );
    default: return '';
  }
}